import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import forOwn from 'lodash/forOwn';
import isEmpty from 'lodash/isEmpty';

import { bindAll } from '@bibliocommons/utils-react';
import { PrimaryButton } from '@bibliocommons/deprecated-base-buttons';
import { bibShape, userIdentityShape, ugcTagShape } from '@bibliocommons/bc-prop-types';
import TokenizedInput from '@bibliocommons/base-tokenized-input';
import { Personal as PersonalIcon } from '@bibliocommons/deprecated-base-icons';

import BaseUGCEditor from '../BaseUGCEditor';
import TagDisplay from '../../TagDisplay';
import { TAG_TYPES } from '../../../../../constants/TagsConstants';

import './TagsUGCEditor.scss';

function renderHeader() {
  return (
    <h2>
      <FormattedMessage id="add_tags" />
    </h2>
  );
}

function filterTagsByType(tags, type) {
  return tags ? tags.filter(tag => tag.get('type') === type).toArray() : [];
}

export class TagsUGCEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.unsavedTokens = {};
  }

  handleTagSave(type, value) {
    const { bib, tagsActions } = this.props;
    const metadataId = bib.getIn(['briefInfo', 'metadataId']);
    delete this.unsavedTokens[type]; // we assume the tag is saved at this point
    tagsActions.addTags(metadataId, [{ metadataId, type, value }]);
  }

  handleInputChange(type, value) {
    this.unsavedTokens[type] = value;
  }

  handleClose() {
    const { bib, tagsActions } = this.props;
    const metadataId = bib.getIn(['briefInfo', 'metadataId']);
    const tagsData = [];

    if (!isEmpty(this.unsavedTokens)) {
      // prepare all unsaved tokens to be generated and saved
      forOwn(this.unsavedTokens, (value, type) => {
        tagsData.push({ metadataId, value, type });
      });

      tagsActions.addTags(metadataId, tagsData, this.props.handleClose);
    } else {
      this.props.handleClose();
    }
  }

  renderTagDisplay(tag) {
    const { owner, tagsTransactions, tagsActions } = this.props;
    const tagId = tag.get('id');
    const tagType = tag.get('type');

    return (
      <TagDisplay
        key={`tag-${tagType}-${tagId}`}
        tag={tag}
        owner={owner}
        tagsTransactions={tagsTransactions}
        tagsActions={tagsActions}
      />
    );
  }

  renderTokenizedInput(type) {
    const { bib, existingTags, tagsTransactions } = this.props;
    const metadataId = bib.getIn(['briefInfo', 'metadataId']);
    const tags = filterTagsByType(existingTags, type);

    return (
      <TokenizedInput
        id={`${type.toLowerCase()}-tags-field`}
        maxInputLength={50}
        tokens={tags}
        renderToken={this.renderTagDisplay}
        getTokenKey={tag => tag.get('id')}
        isSaving={tagsTransactions.getIn([metadataId, 'isAdding'], false)}
        handleTokenSave={value => this.handleTagSave(type, value)}
        handleInputChange={value => this.handleInputChange(type, value)}
        renderNewToken={value => this.renderTagDisplay(Immutable.Map({ metadataId, type, value }))}
        ariaDescribedBy={`tag_help_${type.toLowerCase()}`}
      />
    );
  }

  render() {
    const { bib } = this.props;

    return (
      <BaseUGCEditor bib={bib} renderHeader={renderHeader} className="cp-tags-ugc-editor">
        <div className="tags-ugc-form">
          <p className="tags-form-help">
            <FormattedMessage id="tags_form_help" />
          </p>

          <div className="form-group">
            <label htmlFor="genre-tags-field">
              <FormattedMessage id="tag_heading_genre" />
            </label>
            <p className="help-info" id="tag_help_genre">
              <FormattedMessage id="tag_help_genre" />
            </p>
            {this.renderTokenizedInput(TAG_TYPES.GENRE)}
          </div>

          <div className="form-group">
            <label htmlFor="mood-tags-field">
              <FormattedMessage id="tag_heading_mood" />
            </label>
            <p className="help-info" id="tag_help_mood">
              <FormattedMessage id="tag_help_mood" />
            </p>
            {this.renderTokenizedInput(TAG_TYPES.MOOD)}
          </div>

          <div className="form-group">
            <label htmlFor="subject-tags-field">
              <FormattedMessage id="tag_heading_subject" />
            </label>
            <p className="help-info" id="tag_help_subject">
              <FormattedMessage id="tag_help_subject" />
            </p>
            {this.renderTokenizedInput(TAG_TYPES.SUBJECT)}
          </div>

          <div className="personal-tags-wrapper">
            <div className="form-group">
              <label htmlFor="personal-tags-field">
                <PersonalIcon /> <FormattedMessage id="tag_heading_personal" />
              </label>
              <p className="help-info" id="tag_help_personal">
                <FormattedMessage id="tag_type_personal_description" /> <FormattedMessage id="tag_help_personal" />
              </p>
              {this.renderTokenizedInput(TAG_TYPES.PERSONAL)}
            </div>
          </div>
        </div>

        <div className="ugc-editor-footer">
          <PrimaryButton handleClick={this.handleClose}>
            <FormattedMessage id="done" />
          </PrimaryButton>
        </div>
      </BaseUGCEditor>
    );
  }
}

TagsUGCEditor.propTypes = {
  bib: bibShape.isRequired,
  owner: userIdentityShape.isRequired,
  existingTags: ImmutablePropTypes.listOf(ugcTagShape),
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  handleClose: PropTypes.func.isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired
};

export default TagsUGCEditor;
